import React from 'react';

/**
 * Enhanced mobile device detection utilities
 * Combines screen size with user agent analysis for accurate mobile detection
 */

/**
 * Detects if the current device is a genuine mobile device
 * @returns {boolean} True if device is mobile (phone or tablet)
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;

  // User Agent based mobile detection
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Mobile patterns for phones and tablets
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /Mobile/i,
    /Tablet/i,
    /Opera Mini/i,
    /IEMobile/i
  ];

  const isMobileUserAgent = mobilePatterns.some(pattern => pattern.test(userAgent));

  // Special iPad detection - newer iOS versions may not show "iPad" in user agent
  const isIPad = /iPad/i.test(userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Touch capability detection
  const hasTouchScreen = 'ontouchstart' in window || 
                        navigator.maxTouchPoints > 0 || 
                        navigator.msMaxTouchPoints > 0;

  // Screen size based detection (phones and small tablets)
  const isSmallScreen = window.innerWidth <= 768;
  
  // Tablet specific detection (larger screens but still mobile devices)
  const isTabletScreen = window.innerWidth > 768 && window.innerWidth <= 1024;
  
  // Device pixel ratio (mobile devices often have higher DPR)
  const hasHighDPR = window.devicePixelRatio && window.devicePixelRatio > 1;

  // Orientation API availability (primarily mobile devices)
  const hasOrientationAPI = 'orientation' in window || 'onorientationchange' in window;

  // Combine indicators for robust mobile detection
  if (isMobileUserAgent || isIPad) {
    // If user agent indicates mobile or we detect iPad, trust it regardless of screen size
    return true;
  }

  // For devices without clear mobile user agent, use multiple indicators
  if (isSmallScreen && (hasTouchScreen || hasHighDPR || hasOrientationAPI)) {
    return true;
  }

  // Detect tablets (touch + larger screen + mobile indicators)
  if (isTabletScreen && hasTouchScreen && (hasHighDPR || hasOrientationAPI)) {
    return true;
  }

  return false;
};

/**
 * Detects if the current screen size is mobile-sized
 * @returns {boolean} True if screen width is mobile-sized
 */
export const isMobileScreenSize = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768;
};

/**
 * iPad-specific detection (works on iPadOS 13+ where the UA reports MacIntel).
 * @returns {boolean}
 */
export const isIPadDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  return /iPad/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Storage key for the iPad-landscape-desktop escape hatch.
 * Read directly from localStorage so non-React callers (like the shouldUseMobileUI
 * gate) work without going through redux.
 */
export const IPAD_DESKTOP_LANDSCAPE_KEY = 'iPadDesktopLandscape';

/**
 * Trial flag: when true AND the device is an iPad in landscape orientation,
 * render the desktop UI (sidebar etc.) instead of the mobile UI.
 * Defaults to ON. The toggle writes an explicit 'false' to localStorage when
 * a user opts out, so an absent key (first load, never toggled) is treated
 * as enabled.
 */
export const isIPadDesktopLandscapeEnabled = () => {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(IPAD_DESKTOP_LANDSCAPE_KEY) !== 'false';
};

/**
 * Enhanced mobile detection that prioritizes genuine mobile devices
 * Falls back to screen size for desktop browsers in mobile viewport
 * @returns {boolean} True if should use mobile UI
 */
export const shouldUseMobileUI = () => {
  // iPad-landscape escape hatch (trial). When the user has opted in via the
  // AudioDevices toggle, an iPad in landscape orientation renders the desktop
  // layout. Portrait still falls through to the regular mobile detection.
  if (
    typeof window !== 'undefined' &&
    isIPadDevice() &&
    window.innerWidth > window.innerHeight &&
    isIPadDesktopLandscapeEnabled()
  ) {
    return false;
  }

  const genuineMobile = isMobileDevice();
  const mobileScreenSize = isMobileScreenSize();

  // Always use mobile UI for genuine mobile devices (including iPads)
  if (genuineMobile) return true;

  // For non-mobile devices (desktops), use mobile UI only if screen is very small
  // This helps with responsive design while ensuring mobile devices always get mobile UI
  return mobileScreenSize;
};

/**
 * Detects if the device is specifically a phone (vs tablet)
 * @returns {boolean} True if device is a phone
 */
export const isPhoneDevice = () => {
  if (!isMobileDevice()) return false;
  
  // Phone typically has smaller screen and portrait orientation preference
  const isPhoneSize = window.innerWidth <= 480 || 
                     (window.innerWidth <= 768 && window.innerHeight > window.innerWidth);
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const phonePatterns = [
    /iPhone/i,
    /iPod/i,
    /Android.*Mobile/i,
    /Windows Phone/i,
    /BlackBerry/i
  ];
  
  const isPhoneUserAgent = phonePatterns.some(pattern => pattern.test(userAgent));
  
  return isPhoneUserAgent || (isPhoneSize && isMobileDevice());
};

/**
 * Detects if the device is specifically a tablet
 * @returns {boolean} True if device is a tablet
 */
export const isTabletDevice = () => {
  if (!isMobileDevice()) return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // iPad detection
  if (/iPad/i.test(userAgent)) return true;
  
  // Android tablet detection (Android without "Mobile" keyword)
  if (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) return true;
  
  // Generic tablet patterns
  if (/Tablet/i.test(userAgent)) return true;
  
  // Size-based tablet detection for genuine mobile devices
  const isTabletSize = window.innerWidth > 480 && window.innerWidth <= 1024;
  return isTabletSize && !isPhoneDevice();
};

/**
 * Gets device type string for debugging/logging
 * @returns {string} Device type description
 */
export const getDeviceType = () => {
  if (isPhoneDevice()) return 'phone';
  if (isTabletDevice()) return 'tablet';
  if (isMobileDevice()) return 'mobile';
  return 'desktop';
};

/**
 * Hook for reactive mobile detection with window resize support
 * @returns {object} Object with mobile detection state and utilities
 */
export const useMobileDetection = () => {
  const [deviceInfo, setDeviceInfo] = React.useState({
    isMobile: shouldUseMobileUI(),
    isPhone: isPhoneDevice(),
    isTablet: isTabletDevice(),
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    deviceType: getDeviceType()
  });

  React.useEffect(() => {
    const updateDeviceInfo = () => {
      setDeviceInfo({
        isMobile: shouldUseMobileUI(),
        isPhone: isPhoneDevice(),
        isTablet: isTabletDevice(),
        screenWidth: window.innerWidth,
        deviceType: getDeviceType()
      });
    };

    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};

/**
 * Detects if the device is a Samsung Galaxy device
 * @returns {boolean} True if device is a Samsung Galaxy
 */
export const isSamsungGalaxy = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Samsung Galaxy patterns
  const samsungPatterns = [
    /Samsung/i,
    /SM-/i,
    /Galaxy/i,
    /GT-/i,
    /SCH-/i,
    /SPH-/i,
    /SGH-/i
  ];
  
  return samsungPatterns.some(pattern => pattern.test(userAgent));
};

/**
 * Detects if the device has Android gesture navigation
 * @returns {boolean} True if device likely has gesture navigation
 */
export const hasAndroidGestureNavigation = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Android devices with gesture navigation (Android 10+)
  const isAndroid = /Android/i.test(userAgent);
  if (!isAndroid) return false;
  
  // Try to detect Android version
  const androidVersionMatch = userAgent.match(/Android (\d+)/);
  const androidVersion = androidVersionMatch ? parseInt(androidVersionMatch[1]) : 0;
  
  // Android 10+ typically has gesture navigation
  return androidVersion >= 10;
};

/**
 * Gets safe area inset bottom value for the current device
 * @returns {number} Safe area inset bottom in pixels
 */
export const getSafeAreaInsetBottom = () => {
  if (typeof window === 'undefined') return 0;
  
  // Try to get CSS env() value
  if (CSS.supports('padding-bottom', 'env(safe-area-inset-bottom)')) {
    const testEl = document.createElement('div');
    testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
    document.body.appendChild(testEl);
    const computedPadding = window.getComputedStyle(testEl).paddingBottom;
    document.body.removeChild(testEl);
    
    const pixels = parseFloat(computedPadding);
    if (!isNaN(pixels) && pixels > 0) {
      return pixels;
    }
  }
  
  // Fallback detection for devices with gesture navigation
  if (isSamsungGalaxy() || hasAndroidGestureNavigation()) {
    return 34; // Common gesture navigation bar height
  }
  
  return 0;
};

/**
 * Gets the recommended footer bottom margin for the current device
 * @returns {number} Bottom margin in pixels
 */
export const getFooterBottomMargin = () => {
  const safeAreaInset = getSafeAreaInsetBottom();
  
  if (isSamsungGalaxy()) {
    // Samsung Galaxy devices often need extra margin
    return Math.max(safeAreaInset + 20, 40);
  }
  
  if (hasAndroidGestureNavigation()) {
    return Math.max(safeAreaInset + 15, 35);
  }
  
  if (safeAreaInset > 0) {
    return safeAreaInset + 10;
  }
  
  return 0;
};

/**
 * Debug utility to log device and footer positioning information
 * Useful for diagnosing footer positioning issues on different devices
 */
export const debugFooterPositioning = () => {
  if (typeof window === 'undefined') return;
  
  console.log('🔧 Footer Positioning Debug Info:');
  console.log('Device Type:', getDeviceType());
  console.log('Is Samsung Galaxy:', isSamsungGalaxy());
  console.log('Has Android Gesture Nav:', hasAndroidGestureNavigation());
  console.log('Safe Area Inset Bottom:', getSafeAreaInsetBottom());
  console.log('Recommended Footer Bottom Margin:', getFooterBottomMargin());
  console.log('Screen Size:', `${window.innerWidth}x${window.innerHeight}`);
  console.log('Device Pixel Ratio:', window.devicePixelRatio);
  console.log('User Agent:', navigator.userAgent);
  
  // Check if safe area is supported
  if (CSS.supports('padding-bottom', 'env(safe-area-inset-bottom)')) {
    console.log('✅ Safe Area supported');
  } else {
    console.log('❌ Safe Area NOT supported');
  }
  
  // Check viewport height vs window height (can indicate virtual keyboard or system UI)
  const vh = window.innerHeight * 0.01;
  const viewportHeight = vh * 100;
  console.log('Viewport Height:', viewportHeight);
  console.log('Window Inner Height:', window.innerHeight);
  
  if (Math.abs(viewportHeight - window.innerHeight) > 10) {
    console.log('⚠️ Viewport height mismatch detected - possible system UI interference');
  }
};
